"use client";

import { useTranslations } from "next-intl";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import {
  useFlyToPlace,
  useGeocoder,
  type Place,
} from "@/features/geocoder/useGeocoder";
import { useResetPage } from "@/features/items/hooks/usePage";
import { useSelectedItemId } from "@/features/items/hooks/useSelectedItemId";
import { cn } from "@/lib/cn";
import { useSearchMasks } from "../useSearchMasks";
import { useSuggestions, type Suggestion } from "../useSuggestions";

/** How long to wait after a keystroke before asking for suggestions. */
const DEBOUNCE_MS = 300;
/** Below this, a query matches so much that the suggestions are noise. */
const MIN_QUERY = 2;

/**
 * The search control: a collapsed magnifier that expands into an input, the
 * accepted terms as chips, and a suggestions dropdown.
 *
 * Terms accumulate rather than replacing each other — picking a suggestion turns
 * it into a chip and clears the input, so the next one narrows what's left. That
 * is the legacy interaction, and it's why the input is not itself the query.
 *
 * The legacy version drove this with `downshift`. The interaction here is a
 * listbox with arrow keys and Enter, which is small enough to own outright and
 * one fewer dependency whose accessibility we'd be inheriting untested.
 */
export function Search({
  isOpen,
  setOpen,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}) {
  const t = useTranslations();
  const { masks, add, remove } = useSearchMasks();
  const resetPage = useResetPage();
  const { setSelectedItemId } = useSelectedItemId();

  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(-1);

  const activeQuery = query.length >= MIN_QUERY ? query : "";
  const { suggestions } = useSuggestions(activeQuery);
  const { addresses, pois } = useGeocoder(activeQuery);
  const flyToPlace = useFlyToPlace();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(text);
      // The old highlight indexes into a list that is about to be replaced.
      setHighlighted(-1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  // Clicking away closes the input, but only once it is empty — collapsing it
  // with text still in it would lose what the user typed.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isOpen, setOpen]);

  const accept = (value: string) => {
    add(value);
    // A narrower result set almost never has the page the user was on.
    resetPage();
    setText("");
    setQuery("");
    inputRef.current?.focus();
  };

  /**
   * Open the POI, rather than turning it into a search term.
   *
   * That is what a POI-mode search is for: the suggestion *is* the answer, so
   * there is nothing left to narrow. Legacy also pushed the id onto the search
   * masks here, which put a raw guid in the query and among the visible chips —
   * not repeated, since nothing reads it as a term.
   */
  const openPOI = (id: string) => {
    void setSelectedItemId(id);
    setText("");
    setQuery("");
    setOpen(false);
  };

  const goToPlace = (place: Place) => {
    flyToPlace(place);
    setText("");
    setQuery("");
    setOpen(false);
  };

  // One flat list behind the arrow keys, whatever section each entry is drawn in.
  const options: Option[] = [
    ...suggestions,
    ...addresses.map((place) => ({ kind: "place" as const, place })),
    ...pois.map((place) => ({ kind: "place" as const, place })),
  ];

  const choose = (index: number) => {
    const option = options[index];
    if (!option) return;

    if (option.kind === "term") accept(option.value);
    else if (option.kind === "poi") openPOI(option.id);
    else goToPlace(option.place);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setHighlighted((current) => {
        if (options.length === 0) return -1;
        return (current + step + options.length) % options.length;
      });
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (highlighted >= 0) choose(highlighted);
      else if (text.trim()) accept(text.trim());
      return;
    }

    if (event.key === "Escape") {
      if (text) setText("");
      else setOpen(false);
      return;
    }

    // Backspace on an empty input removes the last chip, the way a tag input does.
    if (event.key === "Backspace" && !text && masks.length > 0) {
      remove(masks[masks.length - 1]);
    }
  };

  const isListOpen = isOpen && options.length > 0 && !!activeQuery;

  return (
    <div
      className="search relative flex shrink-0 items-center justify-center pr-1"
      ref={containerRef}
    >
      <div
        className={cn(
          "flex h-8 cursor-pointer bg-white px-0.5 text-xs text-black ring-1 ring-gray-300",
          "transition-all duration-300 ease-in dark:bg-gray-800 dark:text-off-white w-full",
          isListOpen ? "rounded-t-[16px]" : "rounded-[16px]",
          isOpen ? "tablet:w-80" : "w-9"
        )}
      >
        <button
          type="button"
          className="h-8 py-2 focus:outline-none focus-visible:outline focus-visible:outline-blue-500"
          title={t("search.placeholder")}
          aria-label={t("search.placeholder")}
          aria-expanded={isOpen}
          onClick={() => {
            setOpen(!isOpen);
            if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          <i className="icon ic-ios_Search1 p-2 text-[17px] text-black dark:text-off-white" />
        </button>

        {isOpen && (
          <>
            {masks.length > 0 && (
              <div
                className="flex flex-auto shrink-0 items-center overflow-x-auto p-1"
                style={{ scrollbarWidth: "none" }}
              >
                <div className="flex">
                  {masks.map((mask) => (
                    <button
                      type="button"
                      key={mask}
                      className="mr-1 flex flex-row rounded bg-primary p-1 font-bold dynamic-text"
                      onClick={() => remove(mask)}
                      aria-label={t("search.remove", { term: mask })}
                    >
                      {mask}
                      <i
                        className="icon ic-material_ic_close_18px ml-1"
                        aria-hidden
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={isListOpen}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label={t("search.placeholder")}
              placeholder={masks.length > 0 ? "" : t("search.placeholder")}
              className={cn(
                "min-w-0 flex-auto border-0 bg-transparent px-2 text-xs",
                "focus:outline-none focus:ring-0 dark:text-off-white"
              )}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
            />
          </>
        )}
      </div>

      {isListOpen && (
        <ul
          id={listId}
          role="listbox"
          className={cn(
            "absolute left-0 top-8 z-50 w-80 overflow-hidden rounded-b-[16px] bg-white",
            "shadow-lg ring-1 ring-gray-300 dark:bg-gray-800"
          )}
        >
          {options.map((option, index) => {
            // Section headings sit before the first entry of each kind, so the
            // list reads as "terms, then addresses" without nesting listboxes,
            // which would break the arrow keys.
            const heading =
              index === suggestions.length && addresses.length > 0
                ? t("search.addresses")
                : index === suggestions.length + addresses.length &&
                  pois.length > 0
                ? t("search.poisMap")
                : null;

            return (
              <Fragment key={optionKey(option)}>
                {heading && (
                  <li
                    role="presentation"
                    className="px-4 pt-2 text-[10px] font-semibold uppercase text-gray-500"
                  >
                    {heading}
                  </li>
                )}
                <li
                  role="option"
                  // Named explicitly: the count beside a term would otherwise run
                  // into it, and a screen reader would announce "Beaujon7".
                  aria-label={optionLabel(option)}
                  aria-selected={index === highlighted}
                  tabIndex={-1}
                  onMouseEnter={() => setHighlighted(index)}
                  onMouseDown={(event) => {
                    // Before blur, or the input closes and the click never lands.
                    event.preventDefault();
                    choose(index);
                  }}
                  className={cn(
                    "flex cursor-pointer items-center justify-between px-4 py-2 text-xs",
                    index === highlighted
                      ? "bg-primary dynamic-text"
                      : "text-black dark:text-off-white"
                  )}
                >
                  {option.kind === "term" ? (
                    <>
                      <Highlighted text={option.value} query={query} />
                      {/* Only a term has a count: it says how far picking it
                          would narrow the list. A POI is one place, and a place
                          the geocoder found isn't in the collection at all. */}
                      <span
                        className="ml-2 rounded-full bg-primary-opacity-25 px-2 py-0.5"
                        aria-hidden
                      >
                        {option.count}
                      </span>
                    </>
                  ) : (
                    <Highlighted text={optionLabel(option)} query={query} />
                  )}
                </li>
              </Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A dropdown row, whichever of the three kinds it is. */
type Option = Suggestion | { kind: "place"; place: Place };

/** What the row reads as, and what a screen reader announces for it. */
function optionLabel(option: Option): string {
  if (option.kind === "term") return option.value;
  if (option.kind === "poi") return option.title;
  return option.place.name;
}

function optionKey(option: Option): string {
  if (option.kind === "term") return `term:${option.value}`;
  // Prefixed, because a POI and a geocoded place can share an id — and two POIs
  // can share a title, so the id is what has to be keyed on.
  if (option.kind === "poi") return `poi:${option.id}`;
  return `place:${option.place.id}`;
}

/**
 * The suggestion with the typed part in bold.
 *
 * Built by splitting rather than by injecting `<strong>` into a string: the
 * legacy version interpolated the user's own input into a regular expression and
 * then rendered the result as HTML, which turns a search for `<b` into markup.
 */
function Highlighted({ text, query }: { text: string; query: string }) {
  const at = text.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return <span>{text}</span>;

  return (
    <span>
      {text.slice(0, at)}
      <strong>{text.slice(at, at + query.length)}</strong>
      {text.slice(at + query.length)}
    </span>
  );
}
